<template>
  <div class="PmInputWithStatusPure">
    <PmInputPure
      class="PmInputWithStatusPure-input"
      :value="valueNormalized"
      :label="label"
      :disabled="disabled"
      @input="setText"
    />

    <PmDropdownPure
      class="PmInputWithStatusPure-dropdown"
      :options="statusOptions"
      :value="statusId"
      :label="label ? `${label} Status` : undefined"
      :has-reset="true"
      :disabled="disabled"
      @input="setStatus"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  STATUS_TRAVEL_AND_HOTEL_LOOKUP,
  STATUS_TRAVEL_AND_HOTEL_LOOKUP_LABEL,
} from '@/constants/persoplan'
import { extractStatusAndValueFromString } from '@/utilities/string'
import { lookup } from '@/utilities/misc'
import PmInputPure from '@/components/basics/PmInput/PmInputPure.vue'
import PmDropdownPure, {
  type Emit as EmitDropdownPure,
} from '@/components/basics/PmDropdownPure.vue'
import type { Nilable } from '@/types/misc'
import { isNil } from 'lodash-es'
import { isEmptyString } from '@thomasaull-shared/shared/dom'

export interface Props {
  value?: Nilable<string>
  label: string
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {})

const emit = defineEmits<{
  (event: 'input', value: Nilable<string | number>): void
  (event: 'update:value', value: Nilable<string | number>): void
}>()

const statusOptions = Object.entries(STATUS_TRAVEL_AND_HOTEL_LOOKUP).map(
  ([id, value]) => {
    const label = lookup(value, STATUS_TRAVEL_AND_HOTEL_LOOKUP_LABEL)

    return {
      id: id,
      label: label,
    }
  }
)

const statusId = computed(() => {
  if (!props.value) return
  return extractStatusAndValueFromString(props.value)?.statusId
})

const valueNormalized = computed(() => {
  if (!props.value) return
  return extractStatusAndValueFromString(props.value)?.valueWithoutStatus
})

function setStatus(statusId: EmitDropdownPure['input']) {
  if (typeof statusId !== 'string' && statusId !== undefined) {
    throw new Error('newValue is not string')
  }

  emitInput(normalizeRawValue({ text: valueNormalized.value, statusId }))
}

function setText(text: Nilable<string | number | Date>) {
  if (text instanceof Date) throw new Error('text should not be a Date')
  if (typeof text === 'number') throw new Error('text should not a number')

  emitInput(normalizeRawValue({ text, statusId: statusId.value }))
}

function normalizeRawValue(options: {
  text: Nilable<string>
  statusId: string | undefined
}) {
  const hasText = !isNil(options.text) && !isEmptyString(options.text)
  const hasStatusId = !isNil(options.statusId)

  // Both, text and statusId
  if (hasText && hasStatusId) return `${options.text}|${options.statusId}`

  // Only statusId and no text
  if (hasStatusId) return `|${options.statusId}`

  // Only text and no statusId
  if (hasText) return `${options.text}`

  // Both empty
  return null
}

function emitInput(value: Nilable<string>) {
  emit('input', value)
  emit('update:value', value)
}
</script>

<style lang="scss">
.PmInputWithStatusPure {
  $block: &;

  display: flex;
  gap: 8px;
  min-width: 0;
  align-items: flex-end;

  @include mixin.media('<=400px') {
    flex-direction: column;
    align-items: flex-start;
  }

  &-input {
    width: 70%;

    @include mixin.media('<=400px') {
      width: 100%;
    }
  }

  &-dropdown {
    width: 30%;

    @include mixin.media('<=400px') {
      width: 100%;
    }
  }
}
</style>
